/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type CSSProperties } from 'react';
import { jsx } from '@compiled/react';
import { cssMap } from '@atlaskit/css';
import { ModalBody } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlaskit/platform-feature-flags';
import { type ModalSizesType } from '../../custom-ui';

type ModalContentWrapperProps = {
	children: JSX.Element;
	isResizable?: boolean;
	size: ModalSizesType;
	styles?: CSSProperties;
};

const sizeStyles = cssMap({
	small: {
		height: '20vh',
		minHeight: '320px',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	medium: {
		height: '40vh',
		minHeight: '520px',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	large: {
		height: '70vh',
		minHeight: '720px',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	xlarge: {
		// default behaviour and height from ModalBody is used here.
		// If a static height is used, it will not be responsive and overflow off the screen and viewport height is too small.
		height: '90vh',
		// @ts-expect-error – cssMap types don't support calc() expressions but this is valid CSS
		minHeight: 'calc(100vh - 119px)',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	max: {
		height: '100%',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.small', '3px'),
	},
	fullscreen: {
		height: '100%',
		backgroundColor: token('elevation.surface.overlay'),
	},
});

const resizableStyles = cssMap({
	default: {
		overflow: 'auto',
		height: 'auto',
	},
});

const namedSizes = new Set(Object.keys(sizeStyles) as Array<keyof typeof sizeStyles>);

export const ModalContentWrapper = ({
	children,
	size,
	styles,
	isResizable,
}: ModalContentWrapperProps): JSX.Element => {
	const namedSizeKey = namedSizes.has(size as string) ? (size as keyof typeof sizeStyles) : null;

	if (fg('forge-ui-refactor-custom-ui-modal')) {
		if (!namedSizeKey) {
			return <ModalBody>{children}</ModalBody>;
		}
		const supportsResizable = isResizable && namedSizeKey !== 'max' && namedSizeKey !== 'fullscreen';
		return (
			<div
				css={[sizeStyles[namedSizeKey], supportsResizable && resizableStyles.default]}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={styles}
			>
				{children}
			</div>
		);
	}

	// Legacy switch-based implementation
	switch (size) {
		case 'small':
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<div css={[sizeStyles.small, isResizable && resizableStyles.default]} style={styles}>
					{children}
				</div>
			);
		case 'medium':
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<div css={[sizeStyles.medium, isResizable && resizableStyles.default]} style={styles}>
					{children}
				</div>
			);
		case 'large':
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<div css={[sizeStyles.large, isResizable && resizableStyles.default]} style={styles}>
					{children}
				</div>
			);
		case 'xlarge':
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<div css={[sizeStyles.xlarge, isResizable && resizableStyles.default]} style={styles}>
					{children}
				</div>
			);
		case 'max':
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<div css={[sizeStyles.max]} style={styles}>
					{children}
				</div>
			);
		case 'fullscreen':
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<div css={[sizeStyles.fullscreen]} style={styles}>
					{children}
				</div>
			);
		default:
			return <ModalBody>{children}</ModalBody>;
	}
};

type ModalWidths = 'small' | 'medium' | 'large' | 'xlarge' | 'max';

export const getAtlasKitModalWidth = (
	modalSize: ModalWidths,
): 'small' | 'medium' | 'large' | 'x-large' | '100%' => {
	if (modalSize === 'xlarge') {
		return 'x-large';
	}
	if (modalSize === 'max') {
		return '100%';
	}
	return modalSize;
};

export const getExtensionIcon = (extension: {
	properties?: { config?: { icon?: string }; icon?: string };
}): string | undefined => {
	return extension?.properties?.config?.icon ?? extension?.properties?.icon;
};
